import { CollapsibleComponent } from './../../shared/collapsible/collapsible.component';
import { ProgramCategoryService } from './../../core/service/program-category.service';
import { ProgramService } from '../../core/service/program.service';
import { Component, OnInit, OnDestroy, ViewChild, QueryList, ViewChildren } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { ProgramCategoryList } from '../model/programCategoryList';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { groupBy, sortBy, find, orderBy, sumBy } from 'lodash';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { FilterService } from 'src/app/core/filter/filter.service';
import { Filter } from 'src/app/core/filter/filter';
import { LocalStorageService } from 'ngx-webstorage';
import { ChannelService } from 'src/app/core/service/channel.service';
import { ChannelTotals } from 'src/app/channel/model/channelTotals';
import { ListModeComponent } from 'src/app/shared/list-mode/list-mode.component';
import { State } from '@progress/kendo-data-query';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { ChannelListService } from 'src/app/core/service/grid-data/grid-data-channel.service';
import { Channel } from 'src/app/channel/model/channel';
import { SettingService } from 'src/app/core/service/setting.service';
import { SessionSetting } from 'src/app/core/model/session-setting';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { ProgramWizardComponent } from '../program-wizard/program-wizard.component';

/**
 * Angular component class for Program list.
 */
@Component({
    selector: 'app-program-list',
    templateUrl: './program-list.component.html',
    styleUrls: ['./program-list.component.scss']
})
export class ProgramListComponent implements OnInit, OnDestroy
{
    @ViewChild('listMode') public listMode: ListModeComponent;
    @ViewChildren('collapsibles') public collapsibles: QueryList<CollapsibleComponent>;
    @ViewChild('newProgramWizard') public newProgramWizard: ProgramWizardComponent;

    public loading = true;
    public filterForm: any;
    public channels: any[] = [];
    public allChannels: any[] = [];
    public groupedChannels: any[] = [];
    public showFilterBtnActions = false;
    public breadcrumb = 'ALL';
    public stringLiteralBreadcrumb = false;
    public messageDialogOpened = false;
    public messageSendTo = '';
    public channelTotals = new ChannelTotals();
    public resumeFilters = '';
    public collapseAll = true;
    public channelStatusDialog = false;
    public selectedChannel: Channel;
    public savingMessage = false;
    public newProgramActions = [];

    /**
     * Control if Program Lead have disabled access to program/workflow
     */
    public programDisabled = false;

    /**
     * Program list Kendo grid state.
     */
    public state: State = {
        skip: 0,
        // Initial filter descriptor
        filter: {
            logic: 'and',
            filters: []
        }
    };

    /**
     * List of Group By selection
     */
    public listGroupBy: Array<{ text: string; value: string }>;

    /**
     * List of Performance selection
     */
    public channelPerformances: Array<{ text: string; value: string; sort: number }>;

    /**
     * List of Channel Statuses selection
     */
    public channelStatuses: Array<{ text: string; value: string }>;

    /**
     * List of Channel Sorts selection
     */
    public channelSorts: Array<{ text: string; value: string }>;

    /**
     * Controls whether the advanced feature is shown.
     */
    public enableAdvanced = false;

    /**
     * Program Categories for drop-down.
     */
    public programCategories: ProgramCategoryList[] = [];

    /**
     * Indicates if the save filter dialog is open.
     */
    public filterDialogOpened = false;
    public channelPerformanceList: any[] = [];
    public colors = ['#46aa07', '#45a1e6'];
    public labelTeam = '';
    public labelTarget = '';
    public channelViewType = 'list';
    public filterSelected: Filter;
    public filterAll = true;
    public storageView = '';
    public collapsedSections = new Map<string, boolean>();

    private colorSuccess = '#cf4526';
    private colorWarning = '#e07a1a';
    private colorDanger = '#94be78';
    private colorNotMeasured = '#000000';
    private breadcrumbKey = 'ALL';

    /**
     * Storage prefix
     */
    private storagePrefix = 'view-mode-';

    /**
     * Subscription to Angular component route parameters.
     */
    private routeParamSubscription: Subscription;

    /**
     * When set to true, it will skip applyFilters until all filters are set.
     */
    private disabledApplyFilters = false;

    /**
     * Constructor
     */
    constructor(
        private programService: ProgramService,
        private logger: NGXLogger,
        private translate: TranslateService,
        private fb: UntypedFormBuilder,
        private route: ActivatedRoute,
        private filterService: FilterService,
        private authenticationService: AuthenticationService,
        private programCategoryService: ProgramCategoryService,
        private storageService: LocalStorageService,
        private channelService: ChannelService,
        private channelListService: ChannelListService,
        private settingService: SettingService,
        private messageHandlerService: MessageHandlerService,
        private router: Router,
        private appConfigService: AppConfigService)
    {
        this.state.take = this.appConfigService.getConfig().pageSize;
        this.listGroupBy = this.programService.listGroupBy;
        this.channelPerformances = this.channelService.channelPerformances;
        this.channelStatuses = this.channelService.channelStatuses;
        this.channelSorts = this.channelService.channelSorts;
        this.programCategoryService.getCachedProgramCategories(true).subscribe({
            next: (data) =>
            {
                this.programCategories = data;
            }
        });
        this.initFilterForm();
        this.resetTotals();
    }

    /**
     * Return true if channels are grouped.
     */
    public get isGrouped(): boolean
    {
        return (this.groupBy.value !== undefined && this.groupBy.value !== null && this.groupBy.value !== '');
    }

    /**
     * Return true if channels are sorted.
     */
    public get isSorted(): boolean
    {
        return (this.sortBy.value !== undefined && this.sortBy.value !== null && this.sortBy.value !== '');
    }

    /**
     * GroupBy form control.
     *
     * @returns groupBy form control.
     */
    public get groupBy(): UntypedFormControl
    {
        return this.filterForm.get('groupBy');
    }

    /**
     * FilterByCategory form control.
     *
     * @returns filterByCategory form control.
     */
    public get filterByCategory(): UntypedFormControl
    {
        return this.filterForm.get('filterByCategory');
    }

    /**
     * FilterByPerformance form control.
     *
     * @returns filterByPerformance form control.
     */
    public get filterByPerformance(): UntypedFormControl
    {
        return this.filterForm.get('filterByPerformance');
    }

    /**
     * FilterByStatus form control.
     *
     * @returns filterByStatus form control.
     */
    public get filterByStatus(): UntypedFormControl
    {
        return this.filterForm.get('filterByStatus');
    }

    /**
     * FilterByStatus form control.
     *
     * @returns filterByProgramName form control.
     */
    public get filterByProgramName(): UntypedFormControl
    {
        return this.filterForm.get('filterByProgramName');
    }

    /**
     * FilterByStatus form control.
     *
     * @returns filterByChannelName form control.
     */
    public get filterByChannelName(): UntypedFormControl
    {
        return this.filterForm.get('filterByChannelName');
    }

    /**
     * SortBy form control.
     *
     * @returns sortBy form control.
     */
    public get sortBy(): UntypedFormControl
    {
        return this.filterForm.get('sortBy');
    }

    /**
     * SortDirection form control.
     *
     * @returns sortDirection form control.
     */
    public get sortDirection(): UntypedFormControl
    {
        return this.filterForm.get('sortDirection');
    }

    /**
     * SortDirection form control.
     *
     * @returns sortDirection form control.
     */
    public get typeView(): UntypedFormControl
    {
        return this.filterForm.get('typeView');
    }

    /**
     * Returned the channels count used by the breadcrumb
     */
    public get channelCount(): number
    {
        if (this.isGrouped)
        {
            const channelsCount = sumBy(this.groupedChannels, (o) => o.channels.length);
            return channelsCount;
        }
        else
        {
            return this.channels ? this.channels.length : 0;
        }
    }

    public barColor = (point): string =>
    {
        if (point.category === this.labelTeam)
        {
            return (point.value.target === null || point.value.current >= point.value.target) ? '#589BB3' : '#e96b62';
        }
        else
        {
            return '#3a5161';
        }
    };

    public ngOnInit(): void
    {
        const viewMode = this.storageService.retrieve(`${this.storagePrefix}channel-card-list`);
        this.channelViewType = viewMode || 'list';
        this.routeParamSubscription = this.route.params.subscribe({
            next: (params) =>
            {
                this.filterSelected = undefined;
                this.getChannels(params.filter);
            }
        });

        this.translate.get(['NEW-PROGRAM-ADVANCED', 'NEW-PROGRAM-WIZARD']).subscribe({
            next: (res) =>
            {
                this.newProgramActions.push({
                    actionName: res['NEW-PROGRAM-ADVANCED'],
                    click: (): void => { this.router.navigateByUrl('/program-edit'); }
                });
                this.newProgramActions.push({
                    actionName: res['NEW-PROGRAM-WIZARD'],
                    click: (): void => { this.newProgramWizard.execute(); }
                });
            }
        });

        this.settingService.getCachedSessionSettings().subscribe({
            next: (sessionSetting: SessionSetting) =>
            {
                // Enforce Program Leads access to program/worfklow
                if (sessionSetting.workflowRestrictedUsers !== '')
                {
                    const currentUser = this.authenticationService.currentUser;
                    if (currentUser && currentUser.role === 'ROLE_PROGRAM_LEAD')
                    {
                        const restrictedUsers = sessionSetting.workflowRestrictedUsers.split(',');
                        restrictedUsers.every((id: string) =>
                        {
                            if (parseInt(id, 10) === currentUser.id)
                            {
                                this.programDisabled = true;
                            }
                            return !this.programDisabled;  // Stop iteration when set to false
                        });
                    }
                }
            },
            error: (errorResponse: any) =>
            {
                this.messageHandlerService.displayError(errorResponse);
            }
        });
    }

    public ngOnDestroy(): void
    {
        if (this.routeParamSubscription !== undefined) { this.routeParamSubscription.unsubscribe(); }
    }

    /**
     * Apply Channel Sorting
     *
     * @param channelss Object to sort.
     * @param sort Sort property.
     */
    public applySort(channels: any[], sort: string, sortDirection: string): any[]
    {
        const statistics = (sort === 'openCases' || sort === 'totalTasks' || sort === 'lateTasks');
        const directionArray = [];

        directionArray.push(sortDirection);

        if (statistics)
        {
            return orderBy(channels, [(item) => parseInt(item.statistics[this.sortBy.value],10)], directionArray);
        }
        else if (sort === 'group')
        {
            return orderBy(channels, sort, directionArray);
        }
        else
        {
            return orderBy(channels, [this.sortBy.value], directionArray);
        }
    }

    /**
     * Check if item is selected.
     *
     * @param filterItem Filter to check.
     * @param item Item value to check.
     */
    public isFilterSelected(filterItem: string, itemValue: any): boolean
    {
        return this[filterItem] && this[filterItem].value && this[filterItem].value.includes(itemValue);
    }

    /**
     * Return true a filtered for the filterItem.
     *
     * @param filterItem Filter to check.
     */
    public isFiltered(filterItem: string): boolean
    {
        return this[filterItem] && this[filterItem].value && this[filterItem].value.length > 0;
    }

    /**
     * Clear all filters.
     */
    public clearFilters(): void
    {
        this.filterForm.reset();
        this.showFilterBtnActions = false;
    }

    public getChannels(filter: string): void
    {
        this.loading = true;
        const performance = true;
        this.channelListService.queryByState(this.state, (result: any) =>
        {
            // Process channels
            this.processChannels(result.data, filter);
            this.loading = false;

        }, `&performance=${performance}&active=`);
    }

    /**
     * Return the Channel Status Key for translation.
     *
     * @param channelStatus Current channel status.
     */
    public getChannelStatusKey(channelStatus: string): string
    {
        return this.channelService.getChannelStatusKey(channelStatus);
    }

    public sendMessageToProgramLead(leader: any): void
    {
        if (typeof leader === 'string' || leader instanceof String)
        {
            this.messageSendTo = leader.toString();
        }
        else
        {
            this.messageSendTo = leader.leaderUserName;
        }
        this.messageDialogOpened = true;
    }

    public openFilterScreen(): void
    {
        if (this.filterAll)
        {
            this.filterSelected = undefined;
        }
        this.filterDialogOpened = true;
    }

    public assignView(view: string): void
    {
        this.channelViewType = view;
        this.typeView.setValue(view);
    }

    public onChangeCollapse(event: boolean): void
    {
        if (this.collapsibles !== undefined)
        {
            this.collapsibles.forEach((collapsable) =>
            {
                if (this.collapseAll)
                {
                    if (!collapsable.collapsed)
                    {
                        collapsable.toggleChange();
                    }
                }
                else if (collapsable.collapsed)
                {
                    collapsable.toggleChange();
                }
            });
        }
        this.collapseAll = event;
    }

    public updateProgramCollapse(group: string, event: boolean): void
    {
        this.collapsedSections[group] = event;
        if (!event)
        {
            this.collapseAll = false;
        }
    }

    public refreshProgramList(): void
    {
        this.routeParamSubscription = this.route.params.subscribe({
            next: (params) =>
            {
                this.loading = true;
                const performance = true;
                this.channelListService.queryByState(this.state, (result: any) =>
                {
                    // Process channels
                    this.processChannels(result.data, params.filter);
                    this.loading = false;
                    this.applyFilters();
                }, `&performance=${performance}&active=`);
            }
        });
    }

    public programToggle(group: string): void
    {
        if (this.collapsibles)
        {
            this.collapsibles.forEach((collapsable) =>
            {
                if (collapsable.storageKey === `program-list-${group}`)
                {
                    collapsable.toggleChange();
                    return;
                }
            });
        }
    }

    /**
     * Load the Channels
     */
    private processChannels(data: any, filter: string): void
    {
        this.allChannels = data;

        // determine graph color, background color, and axis range based on "average days to close".
        this.allChannels.forEach(p =>
        {
            if (p.statistics != null && p.statistics.targetDaysToCloseCase > 0)
            {
                const successRatio = p.statistics.averageDaysToCloseCase / p.statistics.targetDaysToCloseCase;
                p.color = successRatio > 1.0 ? this.colorSuccess : successRatio >= 0.9 ? this.colorWarning : this.colorDanger;
                p.backgroundClass =
                    successRatio > 1.0 ? 'bg-danger-channel' : successRatio >= 0.9 ?
                        'bg-warning-channel' : 'bg-success-channel';
            }
            else
            {
                p.color = this.colorNotMeasured;
                p.backgroundClass = 'bg-neutral-channel';
            }

            const channelPerformance = this.channelPerformanceList.find(pp => pp.channelId === p.id);
            if (p.statistics === null)
            {
                p.statistics = { openCases: 0, lateTasks: 0, closedCases: 0, performance: 0, goal: 0 };
            }
            if (channelPerformance !== undefined && p.statistics !== null)
            {
                p.statistics.openCases = channelPerformance.openCases;
                p.statistics.lateTasks = channelPerformance.lateTasks;
                p.statistics.closedCases = channelPerformance.closedCases;
                p.statistics.performance = channelPerformance.performance;
                p.statistics.goal = channelPerformance.channelSuccess;
                this.channelTotals.openCases += p.statistics.openCases;
                this.channelTotals.lateTasks += p.statistics.lateTasks;
                this.channelTotals.closedCases += p.statistics.closedCases;
            }
            p.program.programGrouper = p.program.name + '|' + p.program.id;

            // Set the localized performance group name and sort
            const filterKey = p.backgroundClass.replace('bg-', '').replace('-channel', '');
            const filteredPerformance = find(this.channelPerformances, { value: filterKey });
            if (filteredPerformance)
            {
                p.performanceGroup = filteredPerformance.text;
                p.performanceSort = filteredPerformance.sort;
                p.performanceFilter = filterKey;
            }

            // Set the localized status
            /*const filteredStatus = find(this.channelStatuses, { value: p.status });
            if (filteredStatus)
            {
                p.status = filteredStatus.text;
                // We want Online channels first (inverting sequence because desc sort is being used)
                let seq = 1;
                switch (filteredStatus.value)
                {
                    case 'Online':
                        seq = 3;
                        break;
                    case 'Simulated':
                        seq = 2;
                        break;
                }
                p.defaultSort = seq + `${p.lastModifiedOn}`;
            }*/

            p.bulletGraphAxis = {
                min: 0,
                max: Math.max(p.statistics.averageDaysToCloseCase, p.statistics.targetDaysToCloseCase)
            };

            p.createdOn = new Date(p.createdOn);
        });

        // If all, there is no filter to load.
        if (filter === 'all')
        {
            this.filterAll = true;
            this.storageView = 'channel-card-list';
            this.setFilters();
        }
        else
        {
            this.filterAll = false;
            const currentUser = this.authenticationService.currentUser;
            this.storageView = '';
            this.filterService.getCachedProgramFilters(currentUser.userName).subscribe({
                next: (filters: Filter[]) =>
                {
                    this.setFilters(filter, filters);
                },
                error: (error) =>
                {
                    this.logger.error(error);
                }
            });
        }
    }

    /**
     * Initialization of the filter form controls.
     */
    private initFilterForm(): void
    {
        // Create form controls
        this.filterForm = this.fb.group({
            groupBy: null,
            sortBy: null,
            sortDirection: null,
            filterByCategory: null,
            filterByPerformance: null,
            filterByStatus: null,
            filterByProgramName: null,
            filterByChannelName: null,
            typeView: this.channelViewType
        });

        // Register group/filter changes subscriptions
        this.filterForm.get('groupBy').valueChanges.subscribe({
            next: () =>
            {
                this.applyFilters();
            }
        });

        this.filterForm.get('sortBy').valueChanges.subscribe({
            next: (sortBySelected: string) =>
            {
                // Set default sort direction
                let sortDirection = '';
                if (sortBySelected && sortBySelected !== undefined)
                {
                    switch (sortBySelected)
                    {
                        case 'name':        // Channel name
                        case 'createdOn':
                            sortDirection = 'asc';
                            break;
                        default:
                            sortDirection = 'desc';
                    }
                }
                this.sortDirection.setValue(sortDirection);
                this.applyFilters();
            }
        });

        this.filterForm.get('sortDirection').valueChanges.subscribe({
            next: () =>
            {
                this.applyFilters();
            }
        });

        this.filterForm.get('filterByCategory').valueChanges.subscribe({
            next: () =>
            {
                this.applyFilters();
            }
        });

        this.filterForm.get('filterByPerformance').valueChanges.subscribe({
            next: () =>
            {
                this.applyFilters();
            }
        });

        this.filterForm.get('filterByStatus').valueChanges.subscribe({
            next: () =>
            {
                this.applyFilters();
            }
        });

        this.filterForm.get('filterByProgramName').valueChanges.subscribe({
            next: () =>
            {
                this.applyFilters();
            }
        });

        this.filterForm.get('filterByChannelName').valueChanges.subscribe({
            next: () =>
            {
                this.applyFilters();
            }
        });
    }

    /**
     * Set the filter form criteria and apply them when all set only.
     *
     * @param filter Saved filter.
     */
    private setFilters(filterId: string = null, filters: Filter[] = null): void
    {
        try
        {
            this.disabledApplyFilters = true;    // Disable changes event
            this.clearFilters();

            // If filter to set
            if (filterId && filters)
            {
                const filterItem = filters.find(obj => obj.id.toString() === filterId.toString());
                this.filterSelected = filterItem;
                if (filterItem)
                {
                    const state = JSON.parse(filterItem.state);
                    this.groupBy.setValue(state.groupBy);
                    this.sortBy.setValue(state.sortBy);
                    this.sortDirection.setValue(state.sortDirection);
                    this.filterByCategory.setValue(state.filterByCategory);
                    this.filterByPerformance.setValue(state.filterByPerformance);
                    this.filterByStatus.setValue(state.filterByStatus);
                    this.filterByProgramName.setValue(state.filterByProgramName);
                    this.filterByChannelName.setValue(state.filterByChannelName);
                    this.typeView.setValue(state.typeView);
                    this.breadcrumb = filterItem.name;
                    this.stringLiteralBreadcrumb = true;
                    // Override the view mode based on the filter
                    setTimeout(() =>
                    {
                        if (this.listMode !== undefined)
                        {
                            this.listMode.setListModeValue(state.typeView);
                            this.channelViewType = state.typeView;
                        }
                    });
                }
            }
            else
            {
                this.breadcrumb = this.breadcrumbKey;
                this.stringLiteralBreadcrumb = false;
                this.groupBy.setValue('program');
            }
        }
        finally
        {
            this.disabledApplyFilters = false;   // Enabled changes event
            // Apply Group, Filter and Sort (if any)
            this.applyFilters();
        }
    }

    /**
     * Apply Channel group, filters and sorts.
     */
    private applyFilters(): void
    {
        this.resumeFilters = '';
        // Do not execute when setting group, filter and sort.
        if (this.disabledApplyFilters)
        {
            return;
        }

        let showFilterBtnActions = false;
        let channels = this.allChannels;

        // Apply filters
        if (this.isFiltered('filterByCategory'))
        {
            let categoryDescriptions = '';
            this.filterByCategory.value.forEach(element =>
            {
                const selectedCategory = this.programCategories.find(p => p.id === element);
                if (categoryDescriptions === '')
                {
                    categoryDescriptions = selectedCategory.name;
                }
                else
                {
                    categoryDescriptions += ', ' + selectedCategory.name;
                }
            });
            this.translate.get(['CATEGORY']).subscribe({
                next: (res) =>
                {
                    this.resumeFilters = ((this.resumeFilters === '') ? '' : this.resumeFilters + '. ')
                        + res.CATEGORY + ' = ' + categoryDescriptions;
                }
            });
            channels = channels.filter(x => this.filterByCategory.value.includes(x.categoryId));
            showFilterBtnActions = true;
        }

        if (this.isFiltered('filterByPerformance'))
        {
            this.translate.get(['PERFORMANCE']).subscribe({
                next: (res) =>
                {
                    this.resumeFilters = ((this.resumeFilters === '') ? '' : this.resumeFilters + '. ')
                        + res.PERFORMANCE + ' = ' + this.filterByPerformance.value;
                }
            });
            channels = channels.filter(x => this.filterByPerformance.value.includes(x.performanceFilter));
            showFilterBtnActions = true;
        }

        if (this.isFiltered('filterByStatus'))
        {
            this.translate.get(['STATUS']).subscribe({
                next: (res) =>
                {
                    this.resumeFilters = ((this.resumeFilters === '') ? '' : this.resumeFilters + '. ')
                        + res.STATUS + ' = ' + this.filterByStatus.value;
                }
            });
            channels = channels.filter(x => this.filterByStatus.value.includes(x.status));
            showFilterBtnActions = true;
        }

        if (this.isFiltered('filterByProgramName'))
        {
            this.translate.get(['PROGRAM']).subscribe({
                next: (res) =>
                {
                    this.resumeFilters = ((this.resumeFilters === '') ? '' : this.resumeFilters + '. ')
                        + res.PROGRAM + ' = ' + this.filterByProgramName.value;
                }
            });
            channels = channels.filter(x => x.program.name.toLowerCase().includes(this.filterByProgramName.value.toLowerCase()));
            showFilterBtnActions = true;
        }

        if (this.isFiltered('filterByChannelName'))
        {
            this.translate.get(['CHANNEL']).subscribe({
                next: (res) =>
                {
                    this.resumeFilters = ((this.resumeFilters === '') ? '' : this.resumeFilters + '. ')
                        + res.CHANNEL + ' = ' + this.filterByChannelName.value;
                }
            });
            channels = channels.filter(x => x.name.toLowerCase().includes(this.filterByChannelName.value.toLowerCase()));
            showFilterBtnActions = true;
        }

        let groupLabel = '';
        // Create channel groups if needed
        if (this.isGrouped)
        {
            const aGroup = this.groupBy.value;

            // If performance group, use sort property instead of text.
            const grouped = (aGroup === 'performance') ?
                groupBy(sortBy(channels, `${aGroup}Sort`), `${aGroup}Group`) :
                (aGroup === 'program') ? groupBy(sortBy(channels, `${aGroup}.id`), `${aGroup}.programGrouper`) :
                    groupBy(sortBy(channels, `${aGroup}`), `${aGroup}`);

            if (aGroup === 'performance')
            {
                this.translate.get(['PERFORMANCE']).subscribe({
                    next: (res) =>
                    {
                        groupLabel = res.PERFORMANCE;
                    }
                });
            }
            else if (aGroup === 'program')
            {
                this.translate.get(['PROGRAM']).subscribe({
                    next: (res) =>
                    {
                        groupLabel = res.PROGRAM;
                    }
                });
            }
            else if (aGroup === 'category')
            {
                this.translate.get(['CATEGORY']).subscribe({
                    next: (res) =>
                    {
                        groupLabel = res.CATEGORY;
                    }
                });
            }
            else if (aGroup === 'status')
            {
                this.translate.get(['STATUS']).subscribe({
                    next: (res) =>
                    {
                        groupLabel = res.STATUS;
                    }
                });
            }
            else
            {
                this.translate.get(['GROUP']).subscribe({
                    next: (res) =>
                    {
                        groupLabel = res.GROUP;
                    }
                });
            }
            // Set groupedChannels
            this.groupedChannels = Object.keys(grouped).map(
                group => ({
                    group,
                    channels: (this.isSorted) ?
                        this.applySort(grouped[group], this.sortBy.value, this.sortDirection.value) : grouped[group],
                    totals: new ChannelTotals(),
                })
            );
            this.channels = [];
            this.collapsedSections = new Map<string, boolean>();
            this.groupedChannels.forEach(group =>
            {
                const storedCollapsedStatus = this.storageService.retrieve('collapsible-section-program-list' + group.group);
                this.collapsedSections.set(group.group, storedCollapsedStatus !== null ? storedCollapsedStatus : this.collapseAll);
                if (storedCollapsedStatus !== null && storedCollapsedStatus === false)
                {
                    this.collapseAll = false;
                }
                const totalsGroup = new ChannelTotals();
                group.channels.forEach(p =>
                {
                    totalsGroup.closedCases += p.statistics.closedCases;
                    totalsGroup.lateTasks += p.statistics.lateTasks;
                    totalsGroup.openCases += p.statistics.openCases;
                    totalsGroup.programLead = p.leader;
                    totalsGroup.programName = p.program.name;
                    totalsGroup.programId = p.program.id;
                    totalsGroup.leaderUserName = p.leaderUserName;
                });
                group.totals = totalsGroup;

            });
            this.groupedChannels = this.applySort(this.groupedChannels, 'group', 'asc');

            if (this.resumeFilters !== '')
            {
                this.resumeFilters += ' - (' + this.groupedChannels.length + ') ' + groupLabel;
            }
        }
        else
        {
            this.translate.get(['CHANNELS']).subscribe({
                next: (res) =>
                {
                    groupLabel = res.CHANNELS;
                }
            });

            this.groupedChannels = [];
            // Apply sort
            if (this.isSorted)
            {
                channels = this.applySort(channels, this.sortBy.value, this.sortDirection.value);
            }
            else
            {
                channels = orderBy(channels, ['defaultSort'], 'desc');
            }
            this.channels = channels;

            // Recalculate filtered totals
            this.resetTotals();
            this.channels.forEach(p =>
            {
                this.channelTotals.closedCases += p.statistics.closedCases;
                this.channelTotals.lateTasks += p.statistics.lateTasks;
                this.channelTotals.openCases += p.statistics.openCases;
            });

            if (this.resumeFilters !== '')
            {
                this.resumeFilters += '- (' + channels.length + ') ' + groupLabel;
            }
        }
        // Show filter button actions if channels are filtered, grouped or sorted.
        this.showFilterBtnActions = showFilterBtnActions || this.isSorted || this.isGrouped;
    }

    private resetTotals(): void
    {
        this.channelTotals.openCases = 0;
        this.channelTotals.lateTasks = 0;
        this.channelTotals.closedCases = 0;
    }
}
